import type { ParamValues, PatternCategory, PatternDefinition } from '../types';

/**
 * The pattern registry.
 *
 * Registration is the only coupling point between a pattern and the rest of the
 * app: once a module calls `registerPattern`, the pattern shows up in the
 * browser, gets its controls rendered from its schema and can be used by the
 * evaluator, presets, the worker and the exporters. No other file needs to know
 * it exists.
 */

/** The registry is heterogeneous by nature; each entry pins its own param type. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyPatternDefinition = PatternDefinition<any, any>;

const patterns = new Map<string, AnyPatternDefinition>();

/** Identity helper that keeps a pattern's parameter and prepared types inferred. */
export function definePattern<P extends ParamValues, C = void>(
  definition: PatternDefinition<P, C>,
): PatternDefinition<P, C> {
  return definition;
}

export function registerPattern<P extends ParamValues, C>(
  definition: PatternDefinition<P, C>,
): PatternDefinition<P, C> {
  if (patterns.has(definition.id)) {
    throw new Error(`Pattern "${definition.id}" is already registered.`);
  }
  patterns.set(definition.id, definition as AnyPatternDefinition);
  return definition;
}

export function getPattern(id: string): AnyPatternDefinition | undefined {
  return patterns.get(id);
}

export function requirePattern(id: string): AnyPatternDefinition {
  const definition = patterns.get(id);
  if (!definition) {
    throw new Error(`Unknown pattern "${id}". Registered: ${[...patterns.keys()].join(', ')}`);
  }
  return definition;
}

export function hasPattern(id: string): boolean {
  return patterns.has(id);
}

export function listPatterns(): AnyPatternDefinition[] {
  return [...patterns.values()];
}

export function listPatternsByCategory(category: PatternCategory): AnyPatternDefinition[] {
  return listPatterns().filter((pattern) => pattern.category === category);
}

/** Test-only escape hatch so suites can start from an empty registry. */
export function clearPatterns(): void {
  patterns.clear();
}
